import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getProject, getMilestones, createMilestone, updateMilestone, deleteMilestone,
} from '../api/client'
import { getTasks, createTask, updateTask, deleteTask, getTeamMembers } from '../api/client'
import { getTaskGroups, createTaskGroup, updateTaskGroup, deleteTaskGroup } from '../api/client'
import { getMeetingNotes, createMeetingNote, updateMeetingNote, deleteMeetingNote } from '../api/client'
import { getProjectGantt, downloadGanttTemplate, exportGantt } from '../api/client'
import GanttChart from '../components/gantt/GanttChart'
import { ZOOMS, defaultZoom } from '../components/gantt/ganttScale'
import GanttImportModal from '../components/gantt/GanttImportModal'
import BudgetTab from '../components/budget/BudgetTab'
import StaffingTab from '../components/staffing/StaffingTab'
import BudgetBar from '../components/budget/BudgetBar'
import { getProjectBudget, getProjectAlerts } from '../api/client'
import ProjectAlerts from '../components/alerts/ProjectAlerts'
import { ALERT_TAB } from '../components/alerts/alertText'
import Markdown from '../components/Markdown'
import Modal from '../components/Modal'
import KanbanBoard from '../components/KanbanBoard'
import { formatDate, toISODate } from '../utils/weeks'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import TruncatedCell from '../components/TruncatedCell'
import ColumnFilter from '../components/ColumnFilter'

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUSES = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]
const STATUS_STYLES = {
  todo: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
}
const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-500',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-600',
}

function isSoon(d) {
  if (!d) return false
  const diff = (new Date(d + 'T00:00:00') - new Date().setHours(0, 0, 0, 0)) / 86400000
  return diff >= 0 && diff <= 7
}
function isOverdue(d) {
  if (!d) return false
  return new Date(d + 'T00:00:00') < new Date().setHours(0, 0, 0, 0)
}

// ── Ghost input styles ────────────────────────────────────────────────────────
const ghostText = 'bg-transparent border border-transparent rounded px-1.5 py-1 w-full text-sm text-gray-900 hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-100 transition-colors'
const ghostSelect = 'appearance-none cursor-pointer bg-transparent border border-transparent rounded px-1.5 py-0.5 text-sm text-gray-500 w-full hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none transition-colors'
const ghostDate = 'bg-transparent border border-transparent rounded px-1.5 py-0.5 text-xs hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-100 transition-colors'
const cellInput = 'w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white'

// ── TaskRow ───────────────────────────────────────────────────────────────────
function TaskRow({ task, members, onSaved, onDelete, canEdit, isDragging, onDragStart, onDragEnd, onDragEnterGroup, onDropOnGroup }) {
  const { t } = useTranslation('engineering')
  const [name, setName] = useState(task.name)
  const [status, setStatus] = useState(task.status)
  const [priority, setPriority] = useState(task.priority)
  const [responsibleId, setResponsibleId] = useState(task.responsible_id ?? '')
  const [startDate, setStartDate] = useState(task.start_date ?? '')
  const [deadline, setDeadline] = useState(task.deadline ?? '')

  useEffect(() => {
    setName(task.name); setStatus(task.status); setPriority(task.priority)
    setResponsibleId(task.responsible_id ?? ''); setStartDate(task.start_date ?? ''); setDeadline(task.deadline ?? '')
  }, [task.id, task.name, task.status, task.priority, task.responsible_id, task.start_date, task.deadline])

  const save = (patch) => updateTask(task.id, patch).then(onSaved).catch(() => {})

  const handleNameBlur = () => {
    const trimmed = name.trim()
    if (!trimmed) { setName(task.name); return }
    if (trimmed !== task.name) save({ name: trimmed })
  }

  const deadlineColor = deadline
    ? task.status === 'done' ? 'text-gray-400'
      : isOverdue(deadline) ? 'text-red-600'
      : isSoon(deadline)    ? 'text-amber-600'
      : 'text-gray-500'
    : 'text-gray-300'

  const dragProps = canEdit ? {
    draggable: true,
    onDragStart: e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') { e.preventDefault(); return }
      e.dataTransfer.effectAllowed = 'move'
      onDragStart && onDragStart(task.id)
    },
    onDragEnd: () => onDragEnd && onDragEnd(),
    onDragOver: e => e.preventDefault(),
    onDragEnter: () => onDragEnterGroup && onDragEnterGroup(task.group_id ?? null),
    onDrop: e => { e.preventDefault(); onDropOnGroup && onDropOnGroup(task.group_id ?? null) },
  } : {}

  const statusLabel = (v) => {
    const key = v === 'todo' ? 'projectDetail.statuses.todo' : v === 'in_progress' ? 'projectDetail.statuses.inProgress' : v === 'done' ? 'projectDetail.statuses.done' : null
    return key ? t(key) : v
  }
  const priorityLabel = (v) => {
    const key = v === 'low' ? 'projectDetail.priorities.low' : v === 'medium' ? 'projectDetail.priorities.medium' : v === 'high' ? 'projectDetail.priorities.high' : null
    return key ? t(key) : v
  }

  // Read-only row for engineers
  if (!canEdit) {
    return (
      <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors last:border-b-0">
        <td className="w-6 px-2 py-2"></td>
        <td className="px-3 py-2 text-sm text-gray-900"><TruncatedCell text={task.name} className="max-w-[20rem]" /></td>
        <td className="px-3 py-2">
          <span className={`rounded-full text-xs font-medium px-2.5 py-0.5 ${STATUS_STYLES[task.status]}`}>
            {statusLabel(task.status)}
          </span>
        </td>
        <td className="px-3 py-2">
          <span className={`rounded-full text-xs font-medium px-2.5 py-0.5 ${PRIORITY_STYLES[task.priority]}`}>
            {priorityLabel(task.priority)}
          </span>
        </td>
        <td className="px-3 py-2 text-sm text-gray-500"><TruncatedCell text={task.responsible_name} className="max-w-[12rem]" /></td>
        <td className="px-3 py-2 text-xs text-gray-500">
          {task.start_date ? formatDate(task.start_date) : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2 text-xs">
          {task.deadline
            ? <span className={deadlineColor}>{formatDate(task.deadline)}</span>
            : <span className="text-gray-300">—</span>
          }
        </td>
        <td className="px-3 py-2"></td>
      </tr>
    )
  }

  return (
    <tr
      {...dragProps}
      className={`border-b border-gray-50 group hover:bg-gray-50/60 transition-colors last:border-b-0 ${isDragging ? 'opacity-40' : ''}`}
    >
      <td className="w-6 px-2 py-1.5 cursor-grab">
        <span className="text-gray-300 group-hover:text-gray-400 text-sm select-none transition-colors">⠿</span>
      </td>
      <td className="px-3 py-1.5">
        <input className={ghostText} value={name} title={name} onChange={e => setName(e.target.value)} onBlur={handleNameBlur} />
      </td>
      <td className="px-3 py-1.5">
        <select value={status} onChange={e => { setStatus(e.target.value); save({ status: e.target.value }) }}
          className={`appearance-none cursor-pointer rounded-full text-xs font-medium px-2.5 py-0.5 focus:outline-none hover:opacity-75 transition-opacity ${STATUS_STYLES[status]}`}>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{statusLabel(s.value)}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <select value={priority} onChange={e => { setPriority(e.target.value); save({ priority: e.target.value }) }}
          className={`appearance-none cursor-pointer rounded-full text-xs font-medium px-2.5 py-0.5 focus:outline-none hover:opacity-75 transition-opacity ${PRIORITY_STYLES[priority]}`}>
          {PRIORITIES.map(p => <option key={p.value} value={p.value}>{priorityLabel(p.value)}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <select value={responsibleId} onChange={e => { setResponsibleId(e.target.value); save({ responsible_id: e.target.value ? Number(e.target.value) : null }) }} className={ghostSelect}>
          <option value="">—</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          onBlur={() => { if (startDate !== (task.start_date ?? '')) save({ start_date: startDate || null }) }}
          className={`${ghostDate} text-gray-500`} />
      </td>
      <td className="px-3 py-1.5">
        <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
          onBlur={() => { if (deadline !== (task.deadline ?? '')) save({ deadline: deadline || null }) }}
          className={`${ghostDate} ${deadlineColor}`} />
      </td>
      <td className="px-3 py-1.5 text-right">
        <button onClick={() => onDelete(task)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all text-xs">{t('projectDetail.deleteTaskButton')}</button>
      </td>
    </tr>
  )
}

// ── NewTaskRow ────────────────────────────────────────────────────────────────
// Which tab a URL hash selects. `#staffing` is the one that matters outside
// this page: the Capacity staffing matrix links straight to a project's
// dotacion editor from every column header.
const TAB_HASHES = {
  '#kanban': 'kanban', '#notes': 'notes', '#gantt': 'gantt',
  '#staffing': 'staffing', '#budget': 'budget',
}

const EMPTY_TASK = { name: '', status: 'todo', priority: 'medium', responsible_id: '', start_date: '', deadline: '' }

function NewTaskRow({ members, projectId, groupId, nameRef, onSaved, onCancel }) {
  const { t } = useTranslation('engineering')
  const [form, setForm] = useState(EMPTY_TASK)
  const [saving, setSaving] = useState(false)

  const statusLabel = (v) => {
    const key = v === 'todo' ? 'projectDetail.statuses.todo' : v === 'in_progress' ? 'projectDetail.statuses.inProgress' : v === 'done' ? 'projectDetail.statuses.done' : null
    return key ? t(key) : v
  }
  const priorityLabel = (v) => {
    const key = v === 'low' ? 'projectDetail.priorities.low' : v === 'medium' ? 'projectDetail.priorities.medium' : v === 'high' ? 'projectDetail.priorities.high' : null
    return key ? t(key) : v
  }

  const commit = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      await createTask({
        ...form,
        project_id: projectId,
        group_id: groupId ?? null,
        responsible_id: form.responsible_id ? Number(form.responsible_id) : null,
        start_date: form.start_date || null,
        deadline: form.deadline || null,
      })
      onSaved()
    } catch (_) {}
    setSaving(false)
  }

  return (
    <tr className="border-b border-gray-100 bg-blue-50/40">
      <td className="w-6 px-2 py-2"></td>
      <td className="px-3 py-2"><input ref={nameRef} className={cellInput} placeholder={t('projectDetail.taskNamePlaceholder')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel() }} /></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{STATUSES.map(s => <option key={s.value} value={s.value}>{statusLabel(s.value)}</option>)}</select></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>{PRIORITIES.map(p => <option key={p.value} value={p.value}>{priorityLabel(p.value)}</option>)}</select></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.responsible_id} onChange={e => setForm(f => ({ ...f, responsible_id: e.target.value }))}><option value="">{t('projectDetail.unassigned')}</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></td>
      <td className="px-3 py-2"><input type="date" className={cellInput} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></td>
      <td className="px-3 py-2"><input type="date" className={cellInput} value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={commit} disabled={saving || !form.name.trim()} className="text-xs font-medium text-white bg-gray-900 px-2.5 py-1 rounded hover:bg-gray-700 disabled:opacity-40 mr-1.5">{saving ? t('projectDetail.savingBtn') : t('projectDetail.addBtn')}</button>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-800">{t('projectDetail.cancelBtn')}</button>
      </td>
    </tr>
  )
}

// ── GroupHeaderRow ────────────────────────────────────────────────────────────
function GroupHeaderRow({ group, count, collapsed, onToggle, onRename, onDelete, canEdit, onAddTask, isAddingTask, isDragOver, onDragEnter, onDragLeave, onDrop }) {
  const { t } = useTranslation('engineering')
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(group.name)

  useEffect(() => setName(group.name), [group.name])

  const commitRename = () => {
    const trimmed = name.trim()
    if (!trimmed) { setName(group.name); setRenaming(false); return }
    if (trimmed !== group.name) onRename(trimmed)
    setRenaming(false)
  }

  return (
    <tr
      className={`border-y group/grow transition-colors ${isDragOver ? 'bg-blue-50 border-blue-300' : 'bg-gray-50/70 border-gray-100'}`}
      onDragEnter={onDragEnter}
      onDragOver={e => e.preventDefault()}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) onDragLeave && onDragLeave() }}
      onDrop={e => { e.preventDefault(); onDrop && onDrop() }}
    >
      <td colSpan={8} className="px-3 py-2">
        <div className="flex items-center gap-2">
          <button onClick={onToggle} className="text-gray-400 hover:text-gray-700 w-3 text-[10px] leading-none shrink-0">
            {collapsed ? '▶' : '▼'}
          </button>
          {renaming ? (
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setName(group.name); setRenaming(false) } }}
              className="text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-gray-300 min-w-0"
              placeholder={t('projectDetail.groupNamePlaceholder')}
            />
          ) : (
            <span className="text-sm font-semibold text-gray-700">{group.name}</span>
          )}
          <span className="text-xs text-gray-400 shrink-0">({count})</span>
          {canEdit && !renaming && (
            <div className="hidden group-hover/grow:flex items-center gap-2 ml-1">
              <button onClick={() => setRenaming(true)} className="text-xs text-gray-400 hover:text-gray-700">{t('projectDetail.rename')}</button>
              <button onClick={onDelete} className="text-xs text-gray-400 hover:text-red-500">{t('common:actions.delete')}</button>
            </div>
          )}
          {canEdit && !isAddingTask && (
            <button onClick={onAddTask} className="ml-auto text-xs text-gray-400 hover:text-gray-700 hover:underline shrink-0">{t('projectDetail.addTask')}</button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── NoteCard ──────────────────────────────────────────────────────────────────
function NoteCard({ note, onSaved, onDelete, canEdit = true }) {
  const { t } = useTranslation('engineering')
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ title: note.title, content: note.content || '', meeting_date: note.meeting_date || '' })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!form.title.trim()) return
    setSaving(true)
    try {
      await updateMeetingNote(note.id, { title: form.title.trim(), content: form.content || null, meeting_date: form.meeting_date || null })
      onSaved()
      setEditing(false)
    } catch (_) {}
    setSaving(false)
  }

  if (editing) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex gap-3">
          <input autoFocus className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" placeholder={t('projectDetail.noteTitle')} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <input type="date" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" value={form.meeting_date} onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))} />
        </div>
        <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-200 resize-y" rows={8} placeholder={t('projectDetail.notesPlaceholder')} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
        <p className="text-[11px] text-gray-400 -mt-1">{t('projectDetail.markdownSupported')}</p>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving || !form.title.trim()} className="text-xs font-medium text-white bg-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-700 disabled:opacity-40">{saving ? t('common:actions.saving') : t('common:actions.save')}</button>
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:text-gray-800 px-3 py-1.5">{t('common:actions.cancel')}</button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 group">
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            className={`shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? 'rotate-0' : '-rotate-90'}`}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate">{note.title}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {note.meeting_date && (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{formatDate(note.meeting_date)}</span>
          )}
          {canEdit && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2" onClick={e => e.stopPropagation()}>
              <button onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-gray-900">{t('common:actions.edit')}</button>
              <button onClick={() => onDelete(note)} className="text-xs text-gray-400 hover:text-red-500">{t('common:actions.delete')}</button>
            </div>
          )}
        </div>
      </div>
      {expanded && note.content && (
        <div className="px-4 pb-4 pt-0">
          <Markdown className="border-t border-gray-50 pt-3">{note.content}</Markdown>
        </div>
      )}
    </div>
  )
}

// ── NewNoteForm ───────────────────────────────────────────────────────────────
function NewNoteForm({ projectId, nameRef, onSaved, onCancel }) {
  const { t } = useTranslation('engineering')
  const [form, setForm] = useState({ title: '', content: '', meeting_date: '' })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!form.title.trim()) return
    setSaving(true)
    try {
      await createMeetingNote({ project_id: projectId, title: form.title.trim(), content: form.content || null, meeting_date: form.meeting_date || null })
      onSaved()
    } catch (_) {}
    setSaving(false)
  }

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-4 space-y-3">
      <div className="flex gap-3">
        <input ref={nameRef} autoFocus className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" placeholder={t('projectDetail.noteTitle')} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} onKeyDown={e => e.key === 'Escape' && onCancel()} />
        <input type="date" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" value={form.meeting_date} onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))} />
      </div>
      <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-200 resize-y" rows={8} placeholder={t('projectDetail.notesPlaceholder')} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
      <p className="text-[11px] text-gray-400 -mt-1">{t('projectDetail.markdownSupported')}</p>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving || !form.title.trim()} className="text-xs font-medium text-white bg-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-700 disabled:opacity-40">{saving ? t('common:actions.saving') : t('projectDetail.addNote')}</button>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-800 px-3 py-1.5">{t('common:actions.cancel')}</button>
      </div>
    </div>
  )
}

// ── GanttTab ──────────────────────────────────────────────────────────────────
// The chart itself lives in components/gantt/GanttChart.jsx; this is just the
// tab shell: fetching, the toolbar, and full screen.
function GanttTab({ projectId, projectName, canEdit, onDataChanged }) {
  const { t } = useTranslation('engineering')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [zoom, setZoom] = useState(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [importing, setImporting] = useState(false)
  // Which phases are folded, keyed by phase id. Kept here rather than in the
  // chart so it survives a reload after an import, and per project so two
  // projects do not share one reader's folding.
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`gantt-collapsed-${projectId}`)) || {} }
    catch { return {} }
  })
  const scrollRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const payload = await getProjectGantt(projectId)
      setData(payload)
      // Only choose a zoom the first time; a reload after an edit must not
      // yank the reader back to a different scale.
      setZoom(prev => prev || localStorage.getItem(`gantt-zoom-${projectId}`)
        || defaultZoom(payload.days.length))
    } catch (err) {
      setError(err.response?.data?.detail || t('projectDetail.gantt.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, t])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const chooseZoom = (value) => {
    setZoom(value)
    localStorage.setItem(`gantt-zoom-${projectId}`, value)
  }

  const persistCollapsed = (next) => {
    setCollapsed(next)
    try { localStorage.setItem(`gantt-collapsed-${projectId}`, JSON.stringify(next)) }
    catch { /* a full or blocked localStorage must not break the chart */ }
  }

  const togglePhase = (key) =>
    persistCollapsed({ ...collapsed, [key]: !collapsed[key] })

  const phaseKeys = (data?.phases || [])
    .map((p, i) => p.id ?? `ungrouped-${i}`)
  const allCollapsed = phaseKeys.length > 0 && phaseKeys.every(k => collapsed[k])

  const toggleAll = () =>
    persistCollapsed(allCollapsed
      ? {}
      : Object.fromEntries(phaseKeys.map(k => [k, true])))

  const goToToday = () => {
    if (!data || !scrollRef.current) return
    const i = data.days.indexOf(toISODate(new Date()))
    if (i >= 0) scrollRef.current.scrollLeft = Math.max(0, i * ZOOMS[zoom] - 220)
  }

  if (loading && !data) {
    return <div className="bg-white rounded-xl border border-gray-200 py-14 text-center text-sm text-gray-400">…</div>
  }
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-10 text-center space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={load} className="text-sm text-gray-600 underline">
          {t('projectDetail.gantt.retry')}
        </button>
      </div>
    )
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="flex rounded-lg border border-gray-200 overflow-hidden">
        {['compact', 'normal', 'wide'].map(value => (
          <button
            key={value}
            onClick={() => chooseZoom(value)}
            className={`px-2.5 py-1.5 text-xs ${zoom === value
              ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            {t(`projectDetail.gantt.zoom${value[0].toUpperCase()}${value.slice(1)}`)}
          </button>
        ))}
      </div>
      <button onClick={goToToday}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
        {t('projectDetail.gantt.goToToday')}
      </button>
      <button onClick={toggleAll}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
        {t(allCollapsed ? 'projectDetail.gantt.expandAll'
                        : 'projectDetail.gantt.collapseAll')}
      </button>
      <div className="flex-1" />
      <button onClick={() => downloadGanttTemplate(projectId, projectName)}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
        {t('projectDetail.gantt.downloadTemplate')}
      </button>
      <button onClick={() => exportGantt(projectId, projectName)}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
        {t('projectDetail.gantt.exportExcel')}
      </button>
      {canEdit && (
        <button onClick={() => setImporting(true)}
          className="px-3 py-1.5 text-xs rounded-lg bg-gray-900 text-white hover:bg-gray-800">
          {t('projectDetail.gantt.bulkLoad')}
        </button>
      )}
      <button onClick={() => setFullscreen(f => !f)}
        title={t(fullscreen ? 'projectDetail.gantt.exitFullscreen' : 'projectDetail.gantt.fullscreen')}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
        {fullscreen ? '✕' : '⛶'}
      </button>
    </div>
  )

  const body = (
    <>
      {toolbar}
      {data?.truncated && (
        <p className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
          {t('projectDetail.gantt.truncatedWarning')}
        </p>
      )}
      <GanttChart
        data={data}
        zoom={zoom}
        fullscreen={fullscreen}
        scrollRef={scrollRef}
        collapsed={collapsed}
        onTogglePhase={togglePhase}
      />
    </>
  )

  return (
    <>
      {fullscreen
        ? <div className="fixed inset-0 z-50 bg-white p-4 overflow-auto">{body}</div>
        : body}
      {importing && (
        <GanttImportModal
          projectId={projectId}
          projectName={projectName}
          onClose={() => setImporting(false)}
          onImported={() => { load(); onDataChanged?.() }}
        />
      )}
    </>
  )
}


// ── Main ──────────────────────────────────────────────────────────────────────
const EMPTY_MS = { name: '', due_date: '', description: '' }

export default function ProjectDetail() {
  const { t } = useTranslation('engineering')
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const userRoles = user?.roles || (user?.role ? [user.role] : [])
  const canEdit = userRoles.some(r => r === 'admin' || r === 'pm_po')
  const canEditTasks = !!user

  const [project, setProject] = useState(null)
  const [milestones, setMilestones] = useState([])
  const [tasks, setTasks] = useState([])
  const [groups, setGroups] = useState([])
  const [notes, setNotes] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)

  // The tab lives in the URL hash, so a link can point straight at one and the
  // address bar can be copied -- the Capacity staffing matrix links here at
  // `#staffing` to edit a project's dotacion. Same shape as Capacity.jsx and
  // SCurve.jsx. Read once on mount, which is all a new browser tab needs.
  const [activeTab, setActiveTab] = useState(
    () => (TAB_HASHES[window.location.hash] || 'tasks'))

  const goTab = (next) => {
    setActiveTab(next)
    const hash = next === 'tasks' ? '' : `#${next}`
    history.replaceState(null, '', `${window.location.pathname}${hash}`)
  }
  // Only the two summary bars; the full panel lives in the Presupuesto tab and
  // fetches its own detail. A failure here must leave the page alone.
  const [budget, setBudget] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [msExpanded, setMsExpanded] = useState(true)

  // addingTaskGroupId: null = none | 'ungrouped' | number (group id)
  const [addingTaskGroupId, setAddingTaskGroupId] = useState(null)
  const [addingNote, setAddingNote] = useState(false)
  const taskNameRef = useRef(null)
  const noteNameRef = useRef(null)

  // Collapsed state: { [groupId]: boolean, _ungrouped: boolean }
  const [collapsedGroups, setCollapsedGroups] = useState({})

  // Drag-and-drop across groups
  const [draggingTaskId, setDraggingTaskId] = useState(null)
  const [dragOverGroupId, setDragOverGroupId] = useState(null) // null | groupId | 'ungrouped'

  // Column filters
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [responsibleFilter, setResponsibleFilter] = useState('')
  const [startSort, setStartSort] = useState(null) // null | 'asc' | 'desc'

  const handleTaskDrop = async (targetGroupId) => {
    const tid = draggingTaskId
    setDraggingTaskId(null)
    setDragOverGroupId(null)
    if (tid == null) return
    const task = tasks.find(task => task.id === tid)
    if (!task) return
    const prevGroupId = task.group_id ?? null
    if (prevGroupId === targetGroupId) return
    setTasks(prev => prev.map(task => task.id === tid ? { ...task, group_id: targetGroupId } : task))
    try {
      await updateTask(tid, { group_id: targetGroupId })
    } catch {
      setTasks(prev => prev.map(task => task.id === tid ? { ...task, group_id: prevGroupId } : task))
    }
  }

  // New group inline form
  const [addingGroup, setAddingGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const newGroupRef = useRef(null)

  const [msModal, setMsModal] = useState(null)
  const [msForm, setMsForm] = useState(EMPTY_MS)
  const [msError, setMsError] = useState('')
  const [msSaving, setMsSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    const [proj, ms, ts, grps, mem, ns] = await Promise.all([
      getProject(id), getMilestones(id), getTasks(id), getTaskGroups(id),
      getTeamMembers({ active: true }), getMeetingNotes(id),
    ])
    setProject(proj); setMilestones(ms); setTasks(ts); setGroups(grps); setMembers(mem); setNotes(ns)
    setLoading(false)
  }

  useEffect(() => { load() }, [id])
  useEffect(() => {
    let cancelled = false
    getProjectBudget(id)
      .then(data => { if (!cancelled) setBudget(data) })
      .catch(() => { if (!cancelled) setBudget(null) })
    return () => { cancelled = true }
  }, [id, activeTab])
  useEffect(() => {
    let cancelled = false
    getProjectAlerts(id)
      .then(data => { if (!cancelled) setAlerts(data || []) })
      .catch(() => { if (!cancelled) setAlerts([]) })
    return () => { cancelled = true }
  }, [id, activeTab])
  useEffect(() => { if (addingTaskGroupId !== null) taskNameRef.current?.focus() }, [addingTaskGroupId])
  useEffect(() => { if (addingNote) noteNameRef.current?.focus() }, [addingNote])
  useEffect(() => { if (addingGroup) newGroupRef.current?.focus() }, [addingGroup])

  // Tasks
  const handleTaskSaved = (updated) => setTasks(prev => prev.map(task => task.id === updated.id ? updated : task))
  const handleTaskAdded = async () => { setTasks(await getTasks(id)); setAddingTaskGroupId(null) }
  const handleDeleteTask = async (task) => {
    if (!confirm(t('projectDetail.deleteTaskConfirm', { name: task.name }))) return
    await deleteTask(task.id)
    setTasks(ts => ts.filter(x => x.id !== task.id))
  }

  // Groups
  const handleGroupRename = async (groupId, name) => {
    const updated = await updateTaskGroup(groupId, { name })
    setGroups(prev => prev.map(g => g.id === groupId ? updated : g))
  }
  const handleGroupDelete = async (group) => {
    if (!confirm(t('projectDetail.deleteGroupConfirm', { name: group.name }))) return
    await deleteTaskGroup(group.id)
    setGroups(prev => prev.filter(g => g.id !== group.id))
    setTasks(prev => prev.map(task => task.group_id === group.id ? { ...task, group_id: null } : task))
  }
  const commitAddGroup = async () => {
    const trimmed = newGroupName.trim()
    if (!trimmed) return
    const group = await createTaskGroup({ project_id: Number(id), name: trimmed })
    setGroups(prev => [...prev, group])
    setNewGroupName('')
    setAddingGroup(false)
  }
  const cancelAddGroup = () => { setNewGroupName(''); setAddingGroup(false) }

  // Notes
  const handleNoteSaved = async () => setNotes(await getMeetingNotes(id))
  const handleNoteAdded = async () => { setNotes(await getMeetingNotes(id)); setAddingNote(false) }
  const handleDeleteNote = async (n) => {
    if (!confirm(t('projectDetail.deleteNoteConfirm', { name: n.title }))) return
    await deleteMeetingNote(n.id)
    setNotes(ns => ns.filter(x => x.id !== n.id))
  }

  // Milestones
  const openAddMs = () => { setMsForm(EMPTY_MS); setMsError(''); setMsModal('add') }
  const openEditMs = (ms) => { setMsForm({ name: ms.name, due_date: ms.due_date || '', description: ms.description || '' }); setMsError(''); setMsModal(ms) }
  const handleSaveMs = async () => {
    if (!msForm.name.trim()) { setMsError(t('projectDetail.nameRequired')); return }
    setMsSaving(true)
    try {
      const payload = { ...msForm, due_date: msForm.due_date || null, project_id: Number(id) }
      if (msModal === 'add') await createMilestone(payload)
      else await updateMilestone(msModal.id, { name: msForm.name, due_date: msForm.due_date || null, description: msForm.description || null })
      setMilestones(await getMilestones(id)); setMsModal(null)
    } catch (e) { setMsError(e.response?.data?.detail || 'Save failed') }
    setMsSaving(false)
  }
  const toggleComplete = async (ms) => {
    await updateMilestone(ms.id, { completed: !ms.completed })
    setMilestones(prev => prev.map(m => m.id === ms.id ? { ...m, completed: !m.completed } : m))
  }
  const handleDeleteMs = async (ms) => {
    if (!confirm(t('projectDetail.deleteMilestoneConfirm', { name: ms.name }))) return
    await deleteMilestone(ms.id)
    setMilestones(prev => prev.filter(m => m.id !== ms.id))
  }

  if (loading) return <div className="p-8 text-gray-400 text-sm">{t('projectDetail.loading')}</div>
  if (!project) return <div className="p-8 text-gray-500 text-sm">{t('projectDetail.notFound')}</div>

  const taskTodo = tasks.filter(task => task.status === 'todo').length
  const taskInProgress = tasks.filter(task => task.status === 'in_progress').length
  const taskDone = tasks.filter(task => task.status === 'done').length
  const msCompleted = milestones.filter(m => m.completed).length
  const msOverdue = milestones.filter(m => !m.completed && isOverdue(m.due_date)).length

  const ungroupedTasks = tasks.filter(task => !task.group_id)
  const hasGroups = groups.length > 0

  const statusLbl = (v) => t(`projectDetail.statuses.${v === 'in_progress' ? 'inProgress' : v}`)
  const priorityLbl = (v) => t(`projectDetail.priorities.${v}`)
  const statusFilterOptions = STATUSES.map(s => ({ value: s.value, label: statusLbl(s.value) }))
  const priorityFilterOptions = PRIORITIES.map(p => ({ value: p.value, label: priorityLbl(p.value) }))
  const responsibleFilterOptions = [...new Set(tasks.map(task => task.responsible_name).filter(Boolean))].sort()
  const anyFilterActive = !!(statusFilter || priorityFilter || responsibleFilter)
  const matchesFilters = (task) =>
    (!statusFilter || task.status === statusFilter) &&
    (!priorityFilter || task.priority === priorityFilter) &&
    (!responsibleFilter || task.responsible_name === responsibleFilter)

  const cycleStartSort = () => setStartSort(s => s === null ? 'asc' : s === 'asc' ? 'desc' : null)
  // Returns a sorted copy by start_date when sorting is active; undated tasks always
  // sink to the bottom. Never mutates the source array.
  const applySort = (list) => {
    if (!startSort) return list
    return [...list].sort((a, b) => {
      if (!a.start_date && !b.start_date) return 0
      if (!a.start_date) return 1
      if (!b.start_date) return -1
      return startSort === 'asc'
        ? a.start_date.localeCompare(b.start_date)
        : b.start_date.localeCompare(a.start_date)
    })
  }

  // Table header shared between flat and grouped views
  const TableHead = () => (
    <thead>
      <tr className="bg-gray-50 border-b border-gray-100">
        <th className="w-6 px-2 py-2.5"></th>
        <th className="w-full text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('projectDetail.tableNameHeader')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">
          {t('projectDetail.tableStatusHeader')}
          <ColumnFilter label={t('projectDetail.tableStatusHeader')} value={statusFilter} options={statusFilterOptions} onChange={setStatusFilter} />
        </th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">
          {t('projectDetail.tablePriorityHeader')}
          <ColumnFilter label={t('projectDetail.tablePriorityHeader')} value={priorityFilter} options={priorityFilterOptions} onChange={setPriorityFilter} />
        </th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">
          {t('projectDetail.tableResponsibleHeader')}
          <ColumnFilter label={t('projectDetail.tableResponsibleHeader')} value={responsibleFilter} options={responsibleFilterOptions} onChange={setResponsibleFilter} />
        </th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">
          <button type="button" onClick={cycleStartSort} className={`inline-flex items-center gap-1 transition-colors ${startSort ? 'text-gray-900' : 'hover:text-gray-800'}`}>
            {t('projectDetail.tableStartHeader')}
            <span className="text-[9px] leading-none">{startSort === 'asc' ? '▲' : startSort === 'desc' ? '▼' : ''}</span>
          </button>
        </th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">{t('projectDetail.tableDeadlineHeader')}</th>
        <th className="px-3 py-2.5"></th>
      </tr>
    </thead>
  )

  const TABS = [
    ['tasks', t('projectDetail.tabTasks')],
    ['kanban', t('projectDetail.tabKanban')],
    ['notes', t('projectDetail.tabNotes')],
    ['gantt', t('projectDetail.tabGantt')],
    ['staffing', t('projectDetail.tabStaffing')],
    ['budget', t('projectDetail.tabBudget')],
  ]

  return (
    <div className="p-8">
      {/* Back */}
      <button onClick={() => navigate('/projects')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors mb-5">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        {t('projectDetail.back')}
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
        {project.code && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{project.code}</span>}
        <h1 className="text-[26px] font-bold text-gray-900 tracking-tight leading-tight">{project.nickname || project.name}</h1>
        {project.nickname && <span className="text-sm text-gray-400">{project.name}</span>}
        <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${project.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {project.is_active ? t('projectDetail.active') : t('projectDetail.inactive')}
        </span>
      </div>

      {/* Budget pulse — visible from any tab, once a budget or a spend exists */}
      {budget && (budget.hours.budget != null || budget.money.budget != null
                  || budget.hours.used > 0 || budget.money.used > 0) && (
        <button
          type="button"
          onClick={() => setActiveTab('budget')}
          className="w-full text-left grid gap-4 md:grid-cols-2 px-4 py-3 bg-white rounded-xl border border-gray-200 mb-3 hover:bg-gray-50/60 transition-colors"
        >
          <BudgetBar side={budget.hours} kind="hours" compact />
          <BudgetBar side={budget.money} kind="money" compact />
        </button>
      )}

      {/* Compact dashboard bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 bg-white rounded-xl border border-gray-200 mb-5 text-xs flex-wrap">
        {(project.start_date || project.end_date) && (
          <span className="text-gray-500">
            📅 {project.start_date ? formatDate(project.start_date) : '?'} — {project.end_date ? formatDate(project.end_date) : t('projectDetail.ongoing')}
          </span>
        )}
        {project.po_pm && <span className="text-gray-500">👤 {project.po_pm}</span>}
        {(project.start_date || project.end_date || project.po_pm) && <span className="text-gray-200">|</span>}
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400 font-medium uppercase tracking-wide">{t('projectDetail.tasksLabel')}</span>
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">{t('projectDetail.tasksTodo', { count: taskTodo })}</span>
          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{t('projectDetail.tasksInProgress', { count: taskInProgress })}</span>
          <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">{t('projectDetail.tasksDone', { count: taskDone })}</span>
        </div>
        <span className="text-gray-200">|</span>
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400 font-medium uppercase tracking-wide">{t('projectDetail.milestonesLabel')}</span>
          <span className="text-gray-600 font-medium">{msCompleted}/{milestones.length}</span>
          {msOverdue > 0 && <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">{t('projectDetail.milestonesOverdue', { count: msOverdue })}</span>}
        </div>
        {project.description && <><span className="text-gray-200">|</span><span className="text-gray-400 truncate max-w-xs">{project.description}</span></>}
      </div>

      {/* Whatever the rules flagged, below the summary and above the tabs.
          Clicking a banner opens the tab where that warning gets fixed. */}
      <ProjectAlerts
        alerts={alerts}
        onAlertClick={(alert) => {
          const tab = ALERT_TAB[alert.code]
          if (tab) setActiveTab(tab)
        }}
        className="mb-5"
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => goTab(key)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
              activeTab === key
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-400 hover:text-gray-700'
            }`}
          >
            {label}
            {key === 'notes' && notes.length > 0 && (
              <span className="ml-1.5 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{notes.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tasks tab ── */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          {/* Milestones — collapsible */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <button onClick={() => setMsExpanded(v => !v)} className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  className={`transition-transform duration-200 ${msExpanded ? 'rotate-0' : '-rotate-90'}`}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                {t('projectDetail.milestonesHeader')}
                <span className="text-xs font-normal text-gray-400">({milestones.length})</span>
              </button>
              {canEdit && <button onClick={openAddMs} className="text-xs text-gray-700 hover:underline font-medium">{t('projectDetail.addMilestone')}</button>}
            </div>
            {msExpanded && (
              milestones.length === 0 ? <p className="text-sm text-gray-400 px-5 py-4">{t('projectDetail.noMilestones')}</p> : (
                <ul className="divide-y divide-gray-50">
                  {milestones.map(ms => (
                    <li key={ms.id} className="flex items-center gap-3 px-5 py-2.5 group hover:bg-gray-50 transition-colors">
                      {canEdit
                        ? <input type="checkbox" checked={ms.completed} onChange={() => toggleComplete(ms)} className="accent-gray-900 shrink-0" />
                        : <span className={`w-3 h-3 rounded-full shrink-0 ${ms.completed ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                      }
                      <span className={`flex-1 text-sm ${ms.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{ms.name}</span>
                      {ms.due_date && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${ms.completed ? 'bg-gray-100 text-gray-400' : isOverdue(ms.due_date) ? 'bg-red-100 text-red-600' : isSoon(ms.due_date) ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                          {formatDate(ms.due_date)}{!ms.completed && isOverdue(ms.due_date) ? ' ⚠' : ''}
                        </span>
                      )}
                      {canEdit && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 shrink-0">
                          <button onClick={() => openEditMs(ms)} className="text-xs text-gray-400 hover:text-gray-900">Edit</button>
                          <button onClick={() => handleDeleteMs(ms)} className="text-xs text-gray-400 hover:text-red-500">×</button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>

          {/* Tasks table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-700">{t('projectDetail.tasksHeader')}</span>
              {canEditTasks && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setAddingGroup(true); setAddingTaskGroupId(null) }}
                    disabled={addingGroup}
                    className="text-xs text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
                  >
                    {t('projectDetail.addGroup')}
                  </button>
                  <button
                    onClick={() => setAddingTaskGroupId('ungrouped')}
                    disabled={addingTaskGroupId !== null}
                    className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    {t('projectDetail.newTask')}
                  </button>
                </div>
              )}
            </div>

            {tasks.length === 0 && groups.length === 0 && addingTaskGroupId === null && !addingGroup ? (
              <p className="text-sm text-gray-400 px-5 py-4">{t('projectDetail.noTasksYet')}</p>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {TableHead()}
                <tbody>
                  {!hasGroups ? (
                    // Flat rendering — no groups exist
                    <>
                      {applySort(tasks.filter(matchesFilters)).map(task => (
                        <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask} canEdit={canEditTasks}
                          isDragging={draggingTaskId === task.id}
                          onDragStart={setDraggingTaskId}
                          onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                        />
                      ))}
                      {addingTaskGroupId === 'ungrouped' && (
                        <NewTaskRow members={members} projectId={Number(id)} groupId={null} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                      )}
                    </>
                  ) : (
                    // Grouped rendering
                    <>
                      {groups.map(group => {
                        const groupTasksAll = tasks.filter(task => task.group_id === group.id)
                        const groupTasks = applySort(groupTasksAll.filter(matchesFilters))
                        const isAddingHere = addingTaskGroupId === group.id
                        if (anyFilterActive && groupTasks.length === 0 && !isAddingHere) return null
                        const isCollapsed = !!collapsedGroups[group.id]
                        return (
                          <Fragment key={group.id}>
                            <GroupHeaderRow
                              group={group}
                              count={groupTasksAll.length}
                              collapsed={isCollapsed}
                              onToggle={() => setCollapsedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                              onRename={(name) => handleGroupRename(group.id, name)}
                              onDelete={() => handleGroupDelete(group)}
                              canEdit={canEditTasks}
                              onAddTask={() => { setAddingTaskGroupId(group.id); setCollapsedGroups(prev => ({ ...prev, [group.id]: false })) }}
                              isAddingTask={addingTaskGroupId === group.id}
                              isDragOver={dragOverGroupId === group.id}
                              onDragEnter={() => draggingTaskId != null && setDragOverGroupId(group.id)}
                              onDragLeave={() => setDragOverGroupId(null)}
                              onDrop={() => handleTaskDrop(group.id)}
                            />
                            {!isCollapsed && (
                              <>
                                {groupTasks.map(task => (
                                  <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask} canEdit={canEditTasks}
                                    isDragging={draggingTaskId === task.id}
                                    onDragStart={setDraggingTaskId}
                                    onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                                    onDragEnterGroup={gid => draggingTaskId != null && setDragOverGroupId(gid)}
                                    onDropOnGroup={handleTaskDrop}
                                  />
                                ))}
                                {addingTaskGroupId === group.id && (
                                  <NewTaskRow members={members} projectId={Number(id)} groupId={group.id} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                                )}
                              </>
                            )}
                          </Fragment>
                        )
                      })}

                      {/* Ungrouped section — shown only when there are tasks or adding */}
                      {((anyFilterActive ? ungroupedTasks.filter(matchesFilters).length > 0 : ungroupedTasks.length > 0) || addingTaskGroupId === 'ungrouped') && (
                        <Fragment>
                          <tr
                            className={`border-y transition-colors ${dragOverGroupId === 'ungrouped' ? 'bg-blue-50 border-blue-300' : 'bg-gray-50/70 border-gray-100'}`}
                            onDragEnter={() => draggingTaskId != null && setDragOverGroupId('ungrouped')}
                            onDragOver={e => e.preventDefault()}
                            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverGroupId(null) }}
                            onDrop={e => { e.preventDefault(); handleTaskDrop(null) }}
                          >
                            <td colSpan={8} className="px-3 py-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setCollapsedGroups(prev => ({ ...prev, _ungrouped: !prev._ungrouped }))}
                                    className="text-gray-400 hover:text-gray-700 w-3 text-[10px] leading-none shrink-0"
                                  >
                                    {collapsedGroups._ungrouped ? '▶' : '▼'}
                                  </button>
                                  <span className="text-sm font-semibold text-gray-500">{t('projectDetail.ungrouped')}</span>
                                  <span className="text-xs text-gray-400">({ungroupedTasks.length})</span>
                                </div>
                                {canEditTasks && addingTaskGroupId !== 'ungrouped' && (
                                  <button onClick={() => setAddingTaskGroupId('ungrouped')} className="text-xs text-gray-400 hover:text-gray-700 hover:underline">{t('projectDetail.addTask')}</button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {!collapsedGroups._ungrouped && (
                            <>
                              {applySort(ungroupedTasks.filter(matchesFilters)).map(task => (
                                <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask} canEdit={canEditTasks}
                                  isDragging={draggingTaskId === task.id}
                                  onDragStart={setDraggingTaskId}
                                  onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                                  onDragEnterGroup={gid => draggingTaskId != null && setDragOverGroupId(gid)}
                                  onDropOnGroup={handleTaskDrop}
                                />
                              ))}
                              {addingTaskGroupId === 'ungrouped' && (
                                <NewTaskRow members={members} projectId={Number(id)} groupId={null} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                              )}
                            </>
                          )}
                        </Fragment>
                      )}
                    </>
                  )}

                  {/* New group inline row */}
                  {addingGroup && (
                    <tr className="border-y border-gray-100 bg-gray-50/70">
                      <td colSpan={8} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <input
                            ref={newGroupRef}
                            placeholder={t('projectDetail.groupNamePlaceholder')}
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitAddGroup(); if (e.key === 'Escape') cancelAddGroup() }}
                            className="text-sm text-gray-700 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-300 w-48"
                          />
                          <button onClick={commitAddGroup} disabled={!newGroupName.trim()} className="text-xs font-medium text-white bg-gray-900 px-2.5 py-1 rounded hover:bg-gray-700 disabled:opacity-40">{t('projectDetail.addBtn')}</button>
                          <button onClick={cancelAddGroup} className="text-xs text-gray-500 hover:text-gray-800">{t('projectDetail.cancelBtn')}</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Kanban tab ── */}
      {activeTab === 'kanban' && (
        <KanbanBoard projectId={Number(id)} members={members} canEdit={canEditTasks} />
      )}

      {/* ── Meeting Notes tab ── */}
      {activeTab === 'notes' && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex justify-end">
              <button onClick={() => setAddingNote(true)} disabled={addingNote} className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors">
                {t('projectDetail.newNote')}
              </button>
            </div>
          )}

          {addingNote && (
            <NewNoteForm projectId={Number(id)} nameRef={noteNameRef} onSaved={handleNoteAdded} onCancel={() => setAddingNote(false)} />
          )}

          {notes.length === 0 && !addingNote ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">{t('projectDetail.noMeetingNotes')}</p>
            </div>
          ) : (
            notes.map(n => (
              <NoteCard key={n.id} note={n} onSaved={handleNoteSaved} onDelete={handleDeleteNote} canEdit={canEdit} />
            ))
          )}
        </div>
      )}

      {/* ── Gantt tab ── */}
      {activeTab === 'staffing' && (
        <StaffingTab projectId={project.id} canEdit={canEdit} />
      )}

      {activeTab === 'budget' && (
        <BudgetTab projectId={project.id} canEdit={canEdit} />
      )}

      {activeTab === 'gantt' && (
        <GanttTab
          projectId={project.id}
          projectName={project.code || project.name}
          canEdit={canEdit}
          onDataChanged={load}
        />
      )}

      {/* Milestone modal */}
      {msModal && (
        <Modal title={msModal === 'add' ? t('projectDetail.addMilestoneModal') : t('projectDetail.editMilestoneModal')} onClose={() => setMsModal(null)}>
          <div className="space-y-4">
            {msError && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{msError}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectDetail.milestone.nameLabel')}</label>
              <input autoFocus className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" value={msForm.name} onChange={e => setMsForm(f => ({ ...f, name: e.target.value }))} placeholder={t('projectDetail.milestoneNamePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectDetail.milestone.dueDate')}</label>
              <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" value={msForm.due_date} onChange={e => setMsForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectDetail.milestone.description')}</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none" rows={2} value={msForm.description} onChange={e => setMsForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveMs} disabled={msSaving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50">{msSaving ? t('common:actions.saving') : t('common:actions.save')}</button>
              <button onClick={() => setMsModal(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200">{t('common:actions.cancel')}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
